import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';

import { PRODUCT_TABLE_COLUMNS } from './constants';

// Renders the per-product distribution of packed_units across paquetes + tote.
// Open paquetes always visible. Closed ones collapse under a "Ver N cerradas"
// toggle to keep the row uncluttered when several cajas are already done.
const BreakdownPills = ({ breakdown, i18n }) => {
    const [closedExpanded, setClosedExpanded] = useState(false)
    const openBoxes = breakdown.perBox.filter(b => !b.isClosed)
    const closedBoxes = breakdown.perBox.filter(b => b.isClosed)
    const closedTotal = closedBoxes.reduce((s, b) => s + b.count, 0)
    return (
        <div className="flex flex-wrap gap-1 items-center">
            {openBoxes.map(({ boxNumber, count }) => (
                <span
                    key={`pkg-open-${boxNumber}`}
                    title={i18n.t('generate_shipment.multi_box.product_table.open_box_title')}
                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-blue-50 text-blue-700 border border-blue-200"
                >
                    {i18n.t('generate_shipment.multi_box.per_box_format', { count, n: boxNumber })}
                </span>
            ))}
            {closedBoxes.length > 0 && !closedExpanded && (
                <button
                    type="button"
                    onClick={() => setClosedExpanded(true)}
                    title={i18n.t('generate_shipment.multi_box.product_table.show_closed_boxes')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                >
                    {closedBoxes.length === 1
                        ? i18n.t('generate_shipment.multi_box.per_box_format', { count: closedBoxes[0].count, n: closedBoxes[0].boxNumber })
                        : i18n.t('generate_shipment.multi_box.product_table.closed_boxes_summary', { total: closedTotal, boxes: closedBoxes.length })}
                    <span aria-hidden>✓</span>
                    <ChevronDownIcon className="w-3 h-3" />
                </button>
            )}
            {closedBoxes.length > 0 && closedExpanded && (
                <>
                    {closedBoxes.map(({ boxNumber, count }) => (
                        <span
                            key={`pkg-closed-${boxNumber}`}
                            title={i18n.t('generate_shipment.multi_box.product_table.closed_box_title')}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-green-50 text-green-700 border border-green-200"
                        >
                            {i18n.t('generate_shipment.multi_box.per_box_format', { count, n: boxNumber })}
                            <span aria-hidden>✓</span>
                        </span>
                    ))}
                    <button
                        type="button"
                        onClick={() => setClosedExpanded(false)}
                        title={i18n.t('generate_shipment.multi_box.product_table.hide_closed_boxes')}
                        className="inline-flex items-center px-1 py-0.5 rounded-md text-xs text-green-700 hover:bg-green-50"
                    >
                        <ChevronUpIcon className="w-3 h-3" />
                    </button>
                </>
            )}
            {breakdown.inTote > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600 border border-gray-200">
                    {i18n.t('generate_shipment.multi_box.per_tote_format', { count: breakdown.inTote })}
                </span>
            )}
        </div>
    )
}

// `boxesBreakdown` is provided by the multi-box beta flow:
//   { [sku]: { perBox: [{ boxNumber, count }], inTote } }
// When present, the table switches to the redesigned 4-column layout:
//   SKU | UPC | CANTIDAD (escaneadas / requeridas) | EMPACADO EN
// When absent, the legacy 5-column table renders unchanged.
const ProductsTable = ({ productsWithStock, focusedProduct, boxesBreakdown = null }) => {
    const { i18n } = useTranslation()
    const focusedProductRef = useRef(null)
    const getSku = product => product.product_codes.find(productCode => productCode.sku).sku

    useEffect(() => {
        if (focusedProductRef.current) {
            focusedProductRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [focusedProduct])

    const validateIsFocusedProduct = (productId) => {
        return focusedProduct && focusedProduct.hasOwnProperty("id") && productId === focusedProduct.id
    }

    const isNewDesign = boxesBreakdown != null

    if (isNewDesign) {
        return (
            <table className="w-full">
                <thead>
                    <tr>
                        <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider border-t text-left">SKU</th>
                        <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider border-t text-left">UPC</th>
                        <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider border-t text-center">
                            <div>{i18n.t('generate_shipment.multi_box.product_table.quantity_column')}</div>
                            <div className="text-[10px] text-gray-400 normal-case tracking-normal">{i18n.t('generate_shipment.multi_box.product_table.scanned_required')}</div>
                        </th>
                        <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase tracking-wider border-t text-left">{i18n.t('generate_shipment.multi_box.product_table.packed_in_column')}</th>
                    </tr>
                </thead>

                <tbody>
                    {productsWithStock?.map((product) => {
                        const isRowFocused = validateIsFocusedProduct(product.id)
                        if (isRowFocused) {
                            setTimeout(() => {
                                focusedProductRef.current?.classList.remove("animate-pulse", "bg-gray-50")
                            }, 1000)
                        }
                        const sku = getSku(product)
                        const reviewed = product.reviewedItemsCount || 0
                        const isComplete = reviewed >= product.quantity
                        const breakdown = boxesBreakdown[sku] || { perBox: [], inTote: product.quantity }

                        return (
                            <tr
                                key={`ProductsTable-${product.id}`}
                                ref={isRowFocused ? focusedProductRef : null}
                                className={`${isRowFocused ? "animate-pulse bg-gray-50" : ""}`}
                            >
                                <td className="p-3 whitespace-nowrap border-t text-left">
                                    <span className='rounded-lg bg-blue-100 text-blue-800 select-none px-2.5 py-0.5'>
                                        {sku}
                                    </span>
                                </td>

                                <td className="p-3 whitespace-nowrap border-t text-left">
                                    <span className='rounded-lg bg-blue-100 text-blue-800 select-none px-2.5 py-0.5'>
                                        {product.bar_code === null ? i18n.t('generate_shipment.product_list.no_data') : product.bar_code}
                                    </span>
                                </td>

                                <td className="p-3 whitespace-nowrap border-t text-center">
                                    <span className={`inline-flex items-center gap-1 rounded-full select-none px-2 py-0.5 text-sm ${
                                        isComplete
                                            ? "bg-green-50 text-green-700"
                                            : "bg-yellow-50 text-yellow-700"
                                    }`}>
                                        {reviewed} / {product.quantity}
                                        {isComplete && <span>✓</span>}
                                    </span>
                                </td>

                                <td className="p-3 border-t text-left">
                                    <BreakdownPills breakdown={breakdown} i18n={i18n} />
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    }

    return (
        <table className="w-full ">
            <thead>
                <tr>
                    {PRODUCT_TABLE_COLUMNS.map(column => (
                        <th
                            className="px-6 py-3 font-medium text-gray-500 bg-white tracking-wider border-t"
                            key={column.accessor}
                        >
                            <div className="flex justify-center">{i18n.t(`generate_shipment.product_list.${column['accessor'].toLowerCase()}`)}</div>
                        </th>
                    ))}
                </tr>
            </thead>

            <tbody>
                {productsWithStock?.map((product) => {
                    const isRowFocused = validateIsFocusedProduct(product.id)

                    // Remove the focused effect after some time
                    if (isRowFocused) {
                        setTimeout(() => {
                            focusedProductRef.current?.classList.remove("animate-pulse", "bg-gray-50")
                        }, 1000)
                    }

                    return (
                        <tr
                            key={`ProductsTable-${product.id}`}
                            ref={isRowFocused ? focusedProductRef : null}
                            className={`${isRowFocused ? "animate-pulse bg-gray-50" : ""}`}
                        >
                            <td className="text-center p-2 whitespace-nowrap border-t">
                                <span className='rounded-lg bg-blue-100 text-blue-800 select-none px-2.5 py-0.5'>
                                    {getSku(product)}
                                </span>
                            </td>

                            <td className="text-center p-2 whitespace-nowrap border-t">
                                <span className='rounded-lg bg-blue-100 text-blue-800 select-none px-2.5 py-0.5'>
                                    {product.bar_code === null ? i18n.t('generate_shipment.product_list.no_data') : product.bar_code}
                                </span>
                            </td>

                            <td className="text-center p-2 whitespace-nowrap border-t">
                                {product.quantity}
                            </td>

                            <td className="text-center p-2 whitespace-nowrap border-t">
                                {product.reviewedItemsCount || 0}
                            </td>

                            <td className="text-center p-2 whitespace-nowrap border-t">
                                {product.quantity === product.reviewedItemsCount && (
                                    <span className='rounded-lg bg-green-50 text-green-500 select-none px-2.5 py-0.5'>
                                        {i18n.t('generate_shipment.product_list.complete')}
                                    </span>
                                )}

                                {product.quantity !== product.reviewedItemsCount && (
                                    <span className='rounded-lg bg-yellow-100 text-yellow-800 select-none px-2.5 py-0.5'>
                                        {i18n.t('generate_shipment.product_list.pending')}
                                    </span>
                                )}
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    )
}

export default ProductsTable
